import React, { useMemo, useState } from 'react';
import { ActionModal } from '../../../../components/Modal';
import { User } from '../../../../../types/user';
import { DropdownMultiSelect } from '../../../../components/Dropdown';
import { AddButton, ButtonVariant, CancelButton } from '../../../../components/Button';
import Icon, { IconType } from '../../../../icons/Icon';
import { t } from '../../../../../types/translation/Translator';
import { TranslationKey } from '../../../../../types/translation/TranslationKey';

interface ProductTransactionScanRequiredAddToWhitelistModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  unwhitelistedUsers: User[];
  onSelectionConfirmed: (userIds: Set<string>) => void;
}

export default function ProductTransactionScanRequiredAddToWhitelistModal({
  open,
  setOpen,
  unwhitelistedUsers,
  onSelectionConfirmed: onSelectionConfirmed,
}: ProductTransactionScanRequiredAddToWhitelistModalProps) {
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());

  const handleAdd = (id: string) => {
    setSelectedUserIds(prev => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  };

  const handleRemove = (id: string) => {
    setSelectedUserIds(prev => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const handleSubmit = () => {
    onSelectionConfirmed(selectedUserIds);
    setOpen(false);
    setSelectedUserIds(new Set());
  };

  const handleCancel = () => {
    setOpen(false);
    setSelectedUserIds(new Set());
  };

  const selectedUsersSet = useMemo(
    () => new Set(unwhitelistedUsers.filter(user => selectedUserIds.has(user.id))),
    [unwhitelistedUsers, selectedUserIds],
  );

  const footer = (
    <div className='flex justify-end gap-2'>
      <CancelButton onClick={() => handleCancel()} />
      <AddButton variant={ButtonVariant.primary} onClick={() => handleSubmit()} disabled={selectedUserIds.size === 0} />
    </div>
  );

  return (
    <ActionModal
      open={open}
      setOpen={setOpen}
      title={t(TranslationKey.productTransactionScanRequiredWhitelistAddModalTitle).singular.upper}
      footer={footer}
      className='w-[720px]'
    >
      <DropdownMultiSelect
        items={unwhitelistedUsers.filter(user => !selectedUserIds.has(user.id))}
        label={t(TranslationKey.productTransactionScanRequiredWhitelistAddLabel).singular.upper}
        placeholder={t(TranslationKey.productTransactionScanRequiredWhitelistAddPlaceholder).singular.upper}
        textBuilder={user => user.email}
        values={selectedUsersSet}
        onSelect={selectedUser => handleAdd(selectedUser.id)}
        inputItemBuilder={(user, userText) => (
          <div className='flex items-center gap-2 rounded-2 border p-1 text-sm' key={user.id}>
            <p>{user.email}</p>
            <Icon size='size-4' type={IconType.x} onClick={() => handleRemove(user.id)} />
          </div>
        )}
      />
    </ActionModal>
  );
}
